import axios from "axios";
import { ILog, ComScoreManage, AnalyticsEvent, CcpaStatus } from "@ihr-radioedit/inferno-core";
import type { Store } from "../../../client/stores";

const log = ILog.logger("ComScore Manager");

declare global {
  interface Window {
    COMSCORE: {
      beacon: (opts: object) => void;
    };
    _comscore: [
      {
        c1: string;
        c2: string;
        cs_ucfr: string;
      },
    ];
  }
}

const getUserConsent = (status: CcpaStatus) => {
  switch (status) {
    case CcpaStatus.OptOut:
      return "0";
    case CcpaStatus.OptIn:
      return "1";
    case CcpaStatus.Anonymous:
    default:
      return "";
  }
};

/** Add new members to Base Abstract */
export class ComScoreManager extends ComScoreManage {
  protected _library?: Promise<void>;

  constructor(protected store: Store) {
    super();
    // Check if the current site is configured for ComScore analytics
    const { env } = this.store;

    if (!env.COMSCORE_CLIENT_ID) {
      log.debug("ComScore Client ID not configured");
      this._library = Promise.reject(new Error("ComScore Client ID not configured"));
      return;
    }

    store.onAnalyticsAction.subscribe((event: AnalyticsEvent) => {
      if (event.context === "load_more") {
        this.trackPageView();
      }
    });

    store.comscore = this;
  }

  // Ensure library is loaded once and only once
  loadLibrary() {
    if (this._library) {
      return this._library;
    }

    const { env } = this.store;
    this._library = new Promise((resolve, reject) => {
      window._comscore = window._comscore || [];
      const body = document.querySelector("body");
      const clientId = this.store.env.COMSCORE_CLIENT_ID;
      const cs_ucfr = getUserConsent(this.store.getCcpaStatus());

      if (clientId && body) {
        const srcUrl = `${
          document.location.protocol === "https:" ? "https://sb" : "http://b"
        }.scorecardresearch.com/beacon.js`;
        const script = document.createElement("script");

        script.id = this.comscoreLibId;
        script.async = true;
        script.defer = true;
        script.src = srcUrl;
        script.onload = () => {
          log.debug("ComScore library loaded");

          window._comscore.push({
            c1: "2",
            c2: clientId,
            cs_ucfr,
          });

          resolve();
        };

        script.onerror = (
          event: Event | string,
          source?: string,
          fileno?: number,
          columnNumber?: number,
          error?: Error,
        ) => {
          if (error) {
            log.debug("ComScore library could not be loaded", error.message);
          }
          reject(new Error("ComScore library could not be loaded"));
        };

        body.appendChild(script);
      } else {
        log.debug(`ComScore library could not be loaded: body? ${!!body} configured? ${env.COMSCORE_CLIENT_ID}`);
        reject(new Error("ComScore library could not be loaded"));
      }
    });

    return this._library;
  }

  trackPageView() {
    log.debug("Trigger ComScore page view");
    this.loadLibrary()
      .then(() => this.trigger())
      .catch((e: Error) => log.debug(e.message));
  }

  trigger = () => {
    try {
      const { env } = this.store;

      if (env.COMSCORE_CLIENT_ID && typeof window !== "undefined" && window.COMSCORE) {
        const cs_ucfr = getUserConsent(this.store.getCcpaStatus());
        const timeout = parseInt(this.store.env.REQUEST_TIMEOUT || "0", 10);
        window.COMSCORE.beacon({ c1: "2", c2: env.COMSCORE_CLIENT_ID, cs_ucfr });

        return axios.get("/api/v4/analytics/comscore/", {
          timeout,
        });
      }
    } catch (err) {
      log.error("Failed to load ComScore", err.message);
    }
  };
}
